<script lang="ts" setup generic="T, U extends keyof T">
import { computed, ref, toRef, useTemplateRef } from 'vue';
import { DgrIcon } from '@stockmarkteam/donguri-ui';
import { useHistoryDropdownInput } from '@/components/common/history-dropdown-input/useHistoryDropdownInput';

interface Props {
  histories: T[];
  displayKeyName: U; // 表示用のキー名
  maxVisibleItems?: number;
  enableShiftEnterNewline?: boolean; // Shift + Enter で改行できるようにするか
}

const emit = defineEmits<{
  'delete-history': [index: number];
  'select-from-input': [];
  'select-from-history': [index: number];
}>();

const inputValue = defineModel<string>('inputValue', {
  required: true,
});

const props = defineProps<Props>();
const historyRefs = ref<HTMLElement[]>();
const hoveredIndex = ref(-1);
const histories = toRef(props, 'histories');

const { isHistoryEnabled, deleteHistory, focusIndex, handlers, selectHistory } =
  useHistoryDropdownInput({
    inputValue,
    histories,
    displayKeyName: props.displayKeyName,
    historyRefs,
    emit,
    enableShiftEnterNewline: props.enableShiftEnterNewline,
  });

const ITEM_HEIGHT = 40;
const dropdownHeight = computed(() =>
  props.maxVisibleItems
    ? props.maxVisibleItems * ITEM_HEIGHT
    : (props.histories.length || 0) * ITEM_HEIGHT,
);

const componentRef = useTemplateRef('componentRef');
const maxDropdownWidth = computed(() => {
  return componentRef.value ? componentRef.value.offsetWidth + 'px' : 'none';
});
</script>

<template>
  <div ref="componentRef">
    <slot :value="inputValue" :handlers="handlers">
      <input
        class="c-text-regular--m c-textInput"
        :value="inputValue"
        v-on="handlers"
    /></slot>
    <div
      v-if="isHistoryEnabled && histories.length > 0"
      class="dropdown"
      :class="{ open: true }"
      :style="{
        '--dropdown-height': dropdownHeight + 'px',
      }"
    >
      <div
        v-for="(searchHistory, index) in histories"
        :key="`${index}_${searchHistory[displayKeyName]}`"
        ref="historyRefs"
        class="item"
        :class="{ focused: focusIndex === index }"
        :data-index="index"
        @click.stop="selectHistory(index)"
        @keydown="handlers.keydown"
        @mouseover="hoveredIndex = index"
        @mouseleave="hoveredIndex = -1"
      >
        <div class="item-content">
          <DgrIcon class="clock-icon" name="clock" :size="'default'" />
          <span class="c-text-regular--m history">{{
            searchHistory[displayKeyName]
          }}</span>
        </div>
        <button
          type="button"
          class="delete-button"
          @click.stop="deleteHistory(index)"
          v-if="focusIndex === index || hoveredIndex === index"
        >
          <DgrIcon class="delete-icon" name="times" :size="'xs'" />
        </button>
      </div>
    </div>
  </div>
</template>

<style lang="scss" scoped>
.dropdown {
  position: absolute;
  z-index: var(--z-dropdown);
  background: white;
  width: 100%;
  max-width: v-bind(maxDropdownWidth);
  max-height: 0;
  box-shadow: 0px 1px 5px rgba($color-gray1000, 0.25);
  border-radius: 4px;
  transition: max-height 0.1s ease-out;
  overflow: scroll;

  &.open {
    max-height: var(--dropdown-height);
  }
}

.item {
  display: flex;
  align-items: center;
  justify-content: space-between;
  height: 40px;
  max-width: var(--max-width);
  border: none;
  background: white;
  padding: 0 8px;
  margin: 0;

  &:hover {
    background: $color-gray200;
    cursor: pointer;
  }

  &.focused {
    background: $color-gray400;
  }
}

.item-content {
  display: flex;
  align-items: center;
  flex-grow: 1;
  padding: 0;
  overflow: hidden;
  white-space: nowrap;
}

.clock-icon {
  flex-shrink: 0;
  margin-right: 8px;
}

.delete-button {
  background: none;
  border: none;
  cursor: pointer;
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 0;
  flex-shrink: 0;
  margin: 0 8px;
}

.delete-icon {
  flex-shrink: 0;
}

.history {
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
  flex-grow: 1;
  min-width: 0;
}
</style>
